import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileUpload, UploadProgress } from "@/components/shared/UploadArquivo";
import { TipoArquivo } from "@workflow/types";

interface TarefaUploadDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (file: File) => Promise<unknown>;
  isUploading: boolean;
  /** Progresso do envio (0–100); alimenta a barra durante o upload. */
  progress?: number;
}

export function TarefaUploadDialog({
  isOpen,
  onOpenChange,
  onUpload,
  isUploading,
  progress = 0,
}: TarefaUploadDialogProps) {
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  const handleClose = (open: boolean) => {
    if (isUploading) return;
    onOpenChange(open);
    if (!open) {
      setTimeout(() => setFileToUpload(null), 200); // Reset suave
    }
  };

  const handleSubmit = async () => {
    if (!fileToUpload) return;
    try {
      await onUpload(fileToUpload);
      handleClose(false);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Anexar Arquivo</DialogTitle>
          <DialogDescription>
            Faça o upload de documentos que auxiliem na execução desta tarefa.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isUploading ? (
            <UploadProgress
              percent={progress}
              status={progress >= 100 ? "finalizing" : "uploading"}
              title="Enviando arquivo…"
            />
          ) : (
            <FileUpload
              type={TipoArquivo.TAREFA}
              variant="default"
              onFileChange={setFileToUpload}
              file={fileToUpload}
              maxFileSizeMB={25}
              disabled={isUploading}
            />
          )}
        </div>

        <DialogFooter className="mt-2 gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={isUploading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!fileToUpload || isUploading}
            className="min-w-[140px] gap-2"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
              </>
            ) : (
              "Confirmar Upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
